import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar,
  Rating,
  Alert,
  Button,
} from '@mui/material';
import Header from '../layout/Header';
import { useNavigate, useParams } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { listFeedbacks } from '../../services/firebaseData';

export default function FeedbacksPage({ feedbackType }) {
  const navigate = useNavigate();
  const userId = localStorage.getItem('user_id');
  const [feedbacks, setFeedbacks] = useState([]);
  const [userInfo, serUserInfo] = useState({});
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [loading, setLoading] = useState(true);
  const [averageFeedback, setAverageFeedback] = useState(0);
  const [totalFeedbacks, setTotalFeedbacks] = useState(0);
  const { user_id: initialRequestedId } = useParams();
  const [userRequestedId, setUserRequestedId] = useState(
    initialRequestedId || userId,
  );

  useEffect(() => {
    if (!userId) {
      navigate('/');
    }
    if (!userRequestedId) {
      setUserRequestedId(userId);
    }
  }, [navigate, userId, userRequestedId]);

  useEffect(() => {
    let active = true;
    if (!userRequestedId) return;
    const fetchFeedbacks = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await listFeedbacks(userRequestedId, feedbackType);
        if (!active) return;
        setFeedbacks(data.feedbacks);
        serUserInfo(data.user);
        setTotalFeedbacks(data.total);
        setAverageFeedback(parseFloat(data.average));
      } catch (error) {
        console.error('Error fetching feedbacks:', error);
        if (active) setError('Impossibile caricare i feedback. Riprova.');
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchFeedbacks();
    return () => { active = false; };
  }, [feedbackType, userRequestedId, retry]);

  return (
    <>
      <Box sx={{ p: 2, mx: 'auto', mt: 'var(--cv-top-bar-height)' }}>
        <Header
          title={
            feedbackType === 'received'
              ? 'Feedback Ricevuti'
              : 'Feedback Inviati'
          }
        />
        {loading ? (
          <Typography variant="body1" color="textSecondary">
            Caricamento feedback...
          </Typography>
        ) : error ? (
          <Alert severity="error" action={<Button color="inherit" onClick={() => setRetry((attempt) => attempt + 1)}>Riprova</Button>}>
            {error}
          </Alert>
        ) : (
          <>
            {/* Display average rating */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                my: 2,
                alignItems: 'center',
              }}
            >
              <Avatar
                src={userInfo.photoURL || ''}
                alt={userInfo.displayName}
                sx={{ width: 80, height: 80, mr: 2 }}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {userInfo.displayName}
                </Typography>
                {feedbackType === 'received' && (
                  <Typography
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Rating
                      name="average-feedback"
                      value={averageFeedback}
                      readOnly
                      sx={{ color: 'gold' }}
                    />
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Display total number of feedbacks */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 3,
                mb: 0,
              }}
            >
              <Typography
                sx={{
                  display: 'flex',
                  justifyContent: 'right',
                  alignItems: 'center',
                }}
              >
                {feedbackType === 'received'
                  ? totalFeedbacks + ' feedback ricevuti'
                  : totalFeedbacks + ' feedback inviati'}
              </Typography>
            </Box>

            {/* Feedback list */}
            <List sx={{ mt: 0 }}>
              {feedbacks.map((feedback) => (
                <ListItem
                  key={feedback.id}
                  sx={{
                    padding: '10px 0',
                    borderBottom: '1px solid #ccc',
                  }}
                  onClick={() => navigate('/table/' + feedback.groupId)}
                >
                  <ListItemIcon>
                    <Avatar
                      src={feedback.user?.photoURL || ''}
                      alt={feedback.user?.displayName || ''}
                      sx={{ width: 60, height: 60, mr: 2 }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box>
                        {feedbackType === 'received' && (
                          <Typography variant="body2" color="textSecondary">
                            Da: {feedback.user?.displayName?.trim() || feedback.fromUserId || 'Utente sconosciuto'}
                          </Typography>
                        )}
                        <Typography variant="body2" color="textSecondary">
                          Valutazione:
                          <Rating
                            name="feedback-rating"
                            value={feedback.value}
                            readOnly
                            sx={{ color: 'gold' }}
                          />
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {feedback.note &&
                            'Note: ' + feedback.note}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Tavolo: {feedback.group?.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Luogo: {feedback.group?.location?.place}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Data:{' '}
                          {new Date(
                            feedback.createdAt,
                          ).toLocaleString()}
                        </Typography>
                      </Box>
                    }
                  />
                  <ChevronRightIcon />
                  <Divider />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Box>
    </>
  );
}
